// Headline derivation — PSD §4.2.7 (F5.1–F5.5). Engine layer (framework-free).
import { applyDlom } from './dlom';
import { fullyDilutedShares } from './fullyDiluted';
import { asConvertedShares } from './breakpoints';
import { FMV_MINIMUM_USD, SAFE_HARBOR_WINDOW_DAYS } from './constants';
/** Round up to the nearest cent. */
export function roundUpToCent(x) {
    return Math.ceil(x * 100) / 100;
}
/** Latest preferred issue/transaction price (most recent by issueDate). */
export function latestPreferredPrice(cap) {
    const preferred = cap.instruments
        .filter((i) => i.kind === 'preferred' && i.issuePrice > 0)
        /* v8 ignore next */
        .sort((a, b) => (b.issueDate ?? '').localeCompare(a.issueDate ?? ''));
    if (preferred.length === 0)
        return null;
    const top = preferred[0];
    /* v8 ignore next */
    return top ? top.issuePrice : null;
}
/** Safe-harbor validity window in days (365 default; independent appraisal 365). */
export function safeHarborWindowDays(_safeHarbor) {
    return SAFE_HARBOR_WINDOW_DAYS;
}
/** Sum of a class's allocated value across all tranches. */
function classValue(allocation, isCommon) {
    let total = 0;
    for (const r of allocation) {
        for (const pc of r.perClass) {
            if (isCommon(pc.instrumentId))
                total += pc.value;
        }
    }
    return total;
}
/** Derive the headline outputs (FMV, strike, discount, per-class values). */
export function deriveHeadline(inputs, allocation, V) {
    const issues = [];
    const cap = inputs.capTableSnapshot;
    const fdShares = fullyDilutedShares(cap) || 1;
    const commonIds = new Set(cap.instruments.filter((i) => i.kind === 'common').map((i) => i.id));
    const commonPreDlom = classValue(allocation, (id) => commonIds.has(id));
    const dlomRes = applyDlom(commonPreDlom, inputs.discounts, inputs.assumptions);
    issues.push(...dlomRes.issues);
    const commonPostDlom = dlomRes.value;
    let fmv = commonPostDlom / fdShares;
    if (fmv < FMV_MINIMUM_USD) {
        issues.push({
            code: 'FMV_BELOW_MINIMUM',
            message: `FMV ${fmv.toExponential(3)} below minimum $${FMV_MINIMUM_USD}; clamped.`,
            instrumentId: null,
        });
        fmv = FMV_MINIMUM_USD;
    }
    const strike = Math.max(roundUpToCent(fmv), fmv); // strike >= FMV (rounds up)
    const preferred = latestPreferredPrice(cap);
    const discount = preferred !== null && preferred > 0 ? (preferred - fmv) / preferred : null;
    const window = safeHarborWindowDays(inputs.compliance.safeHarbor);
    const perShareByClass = computePerShareByClass(allocation, cap);
    const headline = {
        totalEquityValue: V,
        commonValuePreDlom: commonPreDlom,
        commonValuePostDlom: commonPostDlom,
        fullyDilutedShares: fdShares,
        commonFmvPerShare: fmv,
        recommendedStrikePrice: strike,
        safeHarborWindowDays: window,
        preferredPricePerShare: preferred,
        commonToPreferredDiscount: discount,
        perShareByClass,
    };
    return { headline, issues };
}
/** Per-share value for every class. */
export function computePerShareByClass(allocation, cap) {
    const totals = new Map();
    for (const r of allocation) {
        for (const pc of r.perClass) {
            totals.set(pc.instrumentId, (totals.get(pc.instrumentId) ?? 0) + pc.value);
        }
    }
    const out = [];
    for (const inst of cap.instruments) {
        const total = totals.get(inst.id) ?? 0;
        const shares = asConvertedShares(inst) || inst.sharesOutstanding || 1;
        out.push({
            instrumentId: inst.id,
            name: inst.name,
            perShareValue: /* v8 ignore next */ shares > 0 ? total / shares : 0,
            totalValue: total,
        });
    }
    return out;
}
//# sourceMappingURL=headline.js.map